import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Ticket, X, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_VOUCHERS = "https://accessoriesgirly.onrender.com/api/vouchers";

export default function CartPage() {
  const [cart, setCart] = useState([]);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  const navigate = useNavigate();

  // Load cart & applied voucher
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    // Ensure cart items have valid price and images
    const validatedCart = savedCart
      .filter((item) => item.price != null && item._id)
      .map((item) => ({
        ...item,
        id: item._id, // Ensure id is consistent
        quantity: item.quantity || 1,
        image: item.images?.[0] || "https://via.placeholder.com/150", // Fallback image
      }));
    setCart(validatedCart);
    localStorage.setItem("cart", JSON.stringify(validatedCart));

    const savedVoucher = JSON.parse(localStorage.getItem("voucher"));
    if (savedVoucher && savedVoucher.amount != null) {
      setAppliedVoucher(savedVoucher.code);
      setVoucherDiscount(savedVoucher.amount);
    }

  }, []);

  // Fetch vouchers from backend
  useEffect(() => {
    const fetchVouchers = async () => {
      try {
        const res = await axios.get(API_VOUCHERS);
        if (res.data.success) setVouchers(res.data.vouchers);
      } catch (err) {
        console.error("Error fetching vouchers:", err);
      }
    };
    fetchVouchers();
  }, []);

  const updateQuantity = (id, delta) => {
    const updatedCart = cart.map((item) => {
      if (item.id === id) {
        const newQty = item.quantity + delta;
        return { ...item, quantity: newQty > 0 ? newQty : 1 };
      }
      return item;
    });
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("storage"));
  };

  const removeFromCart = (id) => {
    const updatedCart = cart.filter((item) => item.id !== id);
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("storage"));
  };

  // Calculate item price considering offerPrice
  const getItemPrice = (item) => {
    return item.offerPrice && item.offerPrice < item.price ? item.offerPrice : item.price;
  };

  // Calculate totals with error handling
  const subtotal = cart.reduce((sum, item) => {
    const price = getItemPrice(item);
    return price != null ? sum + price * item.quantity : sum;
  }, 0);
  const finalTotal = subtotal - voucherDiscount;

  const applyVoucher = async (code) => {
    try {
      const res = await axios.get(`${API_VOUCHERS}/${code}`);
      if (res.data.success) {
        const voucher = res.data.voucher;

        // Calculate discount: for demo, we use minDiscount
        let discount = voucher.minDiscount || 0;

        // Optional: check minPurchase
        const subtotal = cart.reduce((sum, item) => {
          const price = getItemPrice(item);
          return price != null ? sum + price * item.quantity : sum;
        }, 0);
        if (subtotal < voucher.minPurchase) {
          alert(`Minimum purchase ₹${voucher.minPurchase} required for this voucher`);
          return;
        }

        setAppliedVoucher(voucher.code);
        setVoucherDiscount(discount);

        // Save to localStorage
        localStorage.setItem("voucher", JSON.stringify({
          code: voucher.code,
          amount: discount,
        }));

        setVoucherOpen(false);
      }
    } catch (err) {
      alert("Invalid voucher code");
      console.error(err);
    }
  };


  const removeVoucher = () => {
    setAppliedVoucher(null);
    setVoucherDiscount(0);
    localStorage.removeItem("voucher");
  };

  const handleCheckout = () => {
    navigate("/order");
  };

  return (
    <section className="py-12 bg-gradient-to-br from-pink-50 to-purple-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-pink-700 mb-8 text-center">
          Your Shopping Cart
        </h1>

        {cart.length === 0 ? (
          <motion.div
            className="flex flex-col items-center justify-center mt-20 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-gray-700 text-xl sm:text-2xl font-semibold">
              Your cart is empty
            </p>
            <motion.button
              className="bg-pink-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-pink-700 transition-colors"
              onClick={() => navigate("/")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Shop Now
            </motion.button>
          </motion.div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Cart Items */}
            <div className="lg:w-2/3 w-full space-y-4">
              {cart.map((item) => (
                <motion.div
                  key={item.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white p-4 sm:p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full">
                    <img
                      src={item.image || "https://via.placeholder.com/150"}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-800 line-clamp-2">
                        {item.name}
                      </h3>
                      <p className="text-pink-600 font-bold text-base sm:text-lg mt-1">
                        ₹{getItemPrice(item).toFixed(2)}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <motion.button
                          className="px-3 py-1 bg-gray-200 rounded-lg hover:bg-gray-300 text-gray-800 font-semibold"
                          onClick={() => updateQuantity(item.id, -1)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          -
                        </motion.button>
                        <span className="px-4 py-1 border border-gray-300 rounded-lg text-gray-800">
                          {item.quantity}
                        </span>
                        <motion.button
                          className="px-3 py-1 bg-gray-200 rounded-lg hover:bg-gray-300 text-gray-800 font-semibold"
                          onClick={() => updateQuantity(item.id, 1)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          +
                        </motion.button>
                      </div>
                    </div>
                  </div>
                  <motion.button
                    className="mt-4 sm:mt-0 px-4 py-2 bg-red-500 text-white rounded-lg flex items-center gap-2 hover:bg-red-600 transition-colors"
                    onClick={() => removeFromCart(item.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Trash2 className="w-5 h-5" />
                    Remove
                  </motion.button>
                </motion.div>
              ))}
            </div>

            {/* Summary */}
            <motion.div
              className="lg:w-1/3 w-full bg-white p-6 sm:p-8 rounded-2xl shadow-lg"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-pink-700 mb-6">
                Order Summary
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
                </div>
                {appliedVoucher && (
                  <div className="flex justify-between text-gray-700">
                    <span>Voucher ({appliedVoucher})</span>
                    <span className="font-semibold text-green-600">
                      -₹{voucherDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3 flex justify-between text-xl font-bold text-gray-800">
                  <span>Total:</span>
                  <span>₹{finalTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Voucher Section */}
              <motion.div
                className="mt-6 border border-pink-200 rounded-lg p-4 flex items-center gap-3 bg-pink-50 hover:bg-pink-100 cursor-pointer transition-colors"
                onClick={() => !appliedVoucher && setVoucherOpen(!voucherOpen)}
                whileHover={{ scale: 1.02 }}
              >
                <Ticket className="text-pink-600 w-6 h-6" />
                <span className="font-medium text-gray-800 flex-1">
                  {appliedVoucher ? `Applied: ${appliedVoucher}` : "Apply Voucher"}
                </span>
                {appliedVoucher && (
                  <motion.button
                    className="text-red-500 hover:text-red-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeVoucher();
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                )}
              </motion.div>

              {voucherOpen && !appliedVoucher && (
                <motion.div
                  className="mt-4 space-y-3"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {vouchers.length > 0 ? (
                    vouchers.map((v) => (
                      <div
                        key={v.code}
                        className="flex items-center justify-between border border-gray-200 rounded-lg p-3 bg-white"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">{v.code}</p>
                          <p className="text-sm text-gray-600">{v.desc}</p>
                        </div>
                        <motion.button
                          className="px-4 py-2 bg-pink-600 text-white rounded-lg text-sm font-semibold hover:bg-pink-700"
                          onClick={() => applyVoucher(v.code)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Apply
                        </motion.button>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600 text-sm">No vouchers available</p>
                  )}
                </motion.div>
              )}

              <motion.button
                className="mt-6 w-full bg-pink-600 text-white py-3 rounded-lg text-lg font-semibold hover:bg-pink-700 transition-colors"
                onClick={handleCheckout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Proceed to Checkout
              </motion.button>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}
